import type { TaskCategory, TaskStatus, TaskSubStatus, TaskTypeName } from 'src/gql/graphql';
import { XLR8CS__Sub_Category_Task__c } from 'src/types/salesforce-task';

const BUTTON_CTA_BY_TASK_TYPE = {
  file_upload: 'Upload documents',
  initial_intake_form: 'Finish Form',
  onboarding_flow: 'Finish Form',
  account_onboarding: 'Finish Form',
  account_setup: 'Finish Form',
  pre_review_check_in: null,
  swim_request: null,
  trading_request: null,
  money_movement_request: null,
  account_maintenance: null,
  client_info_change: null,
  client_review_presentation: null,
  financial_planning: null,
  investment_proposal_presentation: null,
  other: null,
  other_investment_task: null,
  self_reminder: null,
  client_review: null,
  client_review_meeting: null,
  client_review_meeting_material_request: null,
  investment_proposal_generation: null,
  investment_proposal_request: null,
  client_other: null,
  client_transition_flow: null,
  contract: null,
  contract_change: null,
  data_info_maintenance: null,
  emoney: null,
  investment_management: null,
  investment_management_direct_indexing: null,
  investment_management_wealthcare: null,
  marketing: null,
  reporting: null,
  trades_cash_management: null,
  transfer: null,
  risk_questionnaire: null,
  birthday_reminder: null,
  transition_analysis: null,
  open_new_account: null,
  transfer_of_assets: null,
  alternative_investments_purchase: null,
  eci_household_onboarding: null,
  workflow: null,
  workflow_template: null,
  general: null,
  rejected_fee: null,
  client_billing: null,
  corporate_retirement_plan: null,
};

export const BUTTON_CTA: Record<TaskTypeName, string | null> = BUTTON_CTA_BY_TASK_TYPE;

const GUIDED_FLOW_LINKS_BY_TASK_TYPE = {
  initial_intake_form: '/welcome',
  onboarding_flow: '/onboarding',
  account_onboarding: '/onboarding',
  pre_review_check_in: '/checkin',
  account_setup: '/account-setup',
  risk_questionnaire: '/risk-questionnaires/take',
  file_upload: null,
  swim_request: null, // TODO: double-check this
  trading_request: null,
  money_movement_request: null,
  account_maintenance: null,
  client_info_change: null,
  client_review_presentation: null,
  client_review_meeting: null,
  client_review_meeting_material_request: null,
  investment_proposal_generation: null,
  investment_proposal_request: null,
  other: null,
  other_investment_task: null,
  self_reminder: null,
  client_review: null,
  financial_planning: null,
  investment_proposal_presentation: null,
  client_other: null,
  client_transition_flow: null,
  contract: null,
  contract_change: null,
  data_info_maintenance: null,
  emoney: null,
  investment_management: null,
  investment_management_direct_indexing: null,
  investment_management_wealthcare: null,
  marketing: null,
  reporting: null,
  trades_cash_management: null,
  transfer: null,
  birthday_reminder: null,
  transition_analysis: null,
  open_new_account: null,
  transfer_of_assets: null,
  alternative_investments_purchase: null,
  eci_household_onboarding: null,
  workflow: null,
  workflow_template: null,
  general: null,
  rejected_fee: null,
  client_billing: null,
  corporate_retirement_plan: null,
};

export const GUIDED_FLOW_LINKS: Record<TaskTypeName, string | null> = GUIDED_FLOW_LINKS_BY_TASK_TYPE;

export const TASK_ID_PARAM_SYNCED_TO_SALESFORCE = 'taskId';

const TASK_NAMES_BY_TASK_TYPE = {
  file_upload: 'File Upload',
  initial_intake_form: 'Intake',
  onboarding_flow: 'Onboarding',
  account_onboarding: 'Account Onboarding',
  pre_review_check_in: 'Pre-Review Check In',
  account_setup: 'Account Confirmation',
  swim_request: 'Swim Request', // TODO - double-check this
  trading_request: 'Trade Request',
  money_movement_request: 'Money Movement Request',
  account_maintenance: 'Account Maintenance',
  client_info_change: 'Client Info Change',
  client_review_presentation: 'Client Review Presentation',
  financial_planning: 'Financial Planning',
  investment_proposal_presentation: 'Investment Proposal Presentation',
  other: 'Other',
  other_investment_task: 'Other Investment Task',
  self_reminder: 'Self Reminder',
  client_review: '',
  client_review_meeting: '',
  client_review_meeting_material_request: '',
  investment_proposal_generation: '',
  investment_proposal_request: '',
  client_other: 'Other Client Task',
  client_transition_flow: '',
  contract: '',
  contract_change: '',
  data_info_maintenance: '',
  emoney: '',
  investment_management: '',
  investment_management_direct_indexing: '',
  investment_management_wealthcare: '',
  marketing: '',
  reporting: '',
  trades_cash_management: '',
  transfer: '',
  risk_questionnaire: 'Risk Questionnaire',
  birthday_reminder: 'Birthday Reminder',
  transition_analysis: 'Portfolio Review',
  open_new_account: 'Open New Account',
  transfer_of_assets: 'Transfer of Assets',
  alternative_investments_purchase: 'Alternative Investments Purchase',
  eci_household_onboarding: 'ECI Household Onboarding',
  workflow: 'Workflow',
  workflow_template: '',
  general: 'General',
  rejected_fee: 'Rejected Fee',
  client_billing: 'Client Billing',
  corporate_retirement_plan: 'Corporate Retirement Plans',
};

export const TASK_NAMES: Record<TaskTypeName, string> = TASK_NAMES_BY_TASK_TYPE;

export const TASK_STATUS_TO_LABEL: Record<TaskStatus, string> = {
  todo: 'Not started',
  in_progress: 'In progress',
  done: 'Completed',
  cancelled: 'Cancelled',
  pending: 'Not started',
  failed: 'Not started',
  sent: 'Not started',
  hidden: 'Cancelled',
  draft: 'Draft',
};

// An advisor's Done on an Account Confirmation keeps it in progress until Ops completes it.
export function taskStatusLabel(status: TaskStatus, advisorCompletedAt?: string | null): string {
  return status === 'in_progress' && advisorCompletedAt ? 'Awaiting Ops' : TASK_STATUS_TO_LABEL[status];
}

export const TASK_SUB_STATUS_TO_LABEL: Record<TaskSubStatus, string> = {
  reviewed_ready_for_work: 'Reviewed - Ready for Work',
  active_working: 'Active Working',
  pending_client: 'Pending (Client)',
  pending_advisor: 'Pending (Advisor)',
  pending_custodian: 'Pending (Custodian)',
  paused_blocked: 'Paused / Blocked',
};

export const TASK_CATEGORY_TO_LABEL: Record<TaskCategory, string> = {
  advisor: 'Advisor task',
  client: 'Client task',
  ops: 'Ops task',
};

export const TASK_TYPE_TO_GUIDED_FLOW_URL: Partial<Record<TaskTypeName, string | null>> = {
  initial_intake_form: '/welcome',
  onboarding_flow: '/onboarding',
  client_transition_flow: '/onboarding',
  pre_review_check_in: '/checkin',
  account_setup: '/account-setup',
};

export function taskTypeToReminderEmailTemplate(taskType: TaskTypeName) {
  switch (taskType) {
    case 'initial_intake_form':
      return 'https://docs.google.com/document/d/1SzwbdzK53Ix6wlmRpT0u_emStVWK4EFSnZqGYGQMUuI/edit?usp=sharing';
    case 'pre_review_check_in':
      return 'https://docs.google.com/document/d/1KG41TNWDNZn2oqAdRdjnAZ53637DAP5ULH54KLZw1mE/edit?usp=sharing';
    case 'file_upload':
      return 'https://docs.google.com/document/d/1hi_rWde7ufqMr_3Q8HB8XkHEm3X5o16mYWKePryH3-c/edit?usp=sharing';
    case 'client_transition_flow':
    case 'onboarding_flow':
      return 'https://docs.google.com/document/d/1NEAtISmOoTi4hEHb7wAyCLVElAWvzqdjL4MOP1uTjCY/edit?usp=sharing';
    case 'account_setup':
      return 'https://docs.google.com/document/d/1SlOGMgq9NwplBvRB4UwC0meTRRCEVHCatMugcomxSyU/edit?usp=sharing';
    case 'workflow':
    case 'workflow_template':
    case 'general':
    case 'other':
    case 'swim_request':
    case 'trading_request':
    case 'self_reminder':
    case 'money_movement_request':
    case 'account_maintenance':
    case 'client_info_change':
    case 'financial_planning':
    case 'client_other':
    case 'client_review':
    case 'client_review_meeting':
    case 'client_review_meeting_material_request':
    case 'client_review_presentation':
    case 'contract':
    case 'contract_change':
    case 'data_info_maintenance':
    case 'emoney':
    case 'investment_management':
    case 'investment_management_direct_indexing':
    case 'investment_management_wealthcare':
    case 'investment_proposal_generation':
    case 'investment_proposal_presentation':
    case 'investment_proposal_request':
    case 'marketing':
    case 'reporting':
    case 'trades_cash_management':
    case 'transfer':
    case 'risk_questionnaire':
    case 'other_investment_task':
    case 'birthday_reminder':
    case 'transition_analysis':
    case 'open_new_account':
    case 'transfer_of_assets':
    case 'alternative_investments_purchase':
    case 'eci_household_onboarding':
    case 'account_onboarding':
    case 'rejected_fee':
    case 'client_billing':
    case 'corporate_retirement_plan':
  }
  return '';
}

/**
 * @deprecated - use taskTypeToReminderEmailTemplate instead
 */
export function subCategoryToReminderEmailTemplate(taskSubCategory: XLR8CS__Sub_Category_Task__c) {
  switch (taskSubCategory) {
    case XLR8CS__Sub_Category_Task__c.ClientIntake:
      return 'https://docs.google.com/document/d/1SzwbdzK53Ix6wlmRpT0u_emStVWK4EFSnZqGYGQMUuI/edit?usp=sharing';
    case XLR8CS__Sub_Category_Task__c.ClientPreReviewCheckIn:
      return 'https://docs.google.com/document/d/1KG41TNWDNZn2oqAdRdjnAZ53637DAP5ULH54KLZw1mE/edit?usp=sharing';
    case XLR8CS__Sub_Category_Task__c.ClientFileUpload:
      return 'https://docs.google.com/document/d/1hi_rWde7ufqMr_3Q8HB8XkHEm3X5o16mYWKePryH3-c/edit?usp=sharing';
    case XLR8CS__Sub_Category_Task__c.ClientOnboarding:
      return 'https://docs.google.com/document/d/1NEAtISmOoTi4hEHb7wAyCLVElAWvzqdjL4MOP1uTjCY/edit?usp=sharing';
    case XLR8CS__Sub_Category_Task__c.ClientAccountConfirmation:
      return 'https://docs.google.com/document/d/1SlOGMgq9NwplBvRB4UwC0meTRRCEVHCatMugcomxSyU/edit?usp=sharing';
    case XLR8CS__Sub_Category_Task__c.ClientOther:
    case XLR8CS__Sub_Category_Task__c.ClientRiskQuestionnaire:
    case XLR8CS__Sub_Category_Task__c.SelfReminder:
    case XLR8CS__Sub_Category_Task__c.Other:
    case XLR8CS__Sub_Category_Task__c.OtherInvestmentTask:
    case XLR8CS__Sub_Category_Task__c.MoneyMovement:
    case XLR8CS__Sub_Category_Task__c.NewMoneyMovement:
    case XLR8CS__Sub_Category_Task__c.DEPRECEATED_MoneyMovement:
    case XLR8CS__Sub_Category_Task__c.Trading:
    case XLR8CS__Sub_Category_Task__c.AccountMaintenance:
    case XLR8CS__Sub_Category_Task__c.ClientInfoChange:
    case XLR8CS__Sub_Category_Task__c.FinnancialPlanning:
    case XLR8CS__Sub_Category_Task__c.InvestmentManagementDirectIndexing:
    case XLR8CS__Sub_Category_Task__c.InvestmentManagementSWIM:
    case XLR8CS__Sub_Category_Task__c.ClientReviewPresentation:
    case XLR8CS__Sub_Category_Task__c.InvestmentProposalPresentation:
    case XLR8CS__Sub_Category_Task__c.TransitionAnalysis:
  }
  return '';
}

/**
 * Task types that support an "instructions" field that updates the task's description field.
 * Other tasks that are not here, handle the description field differently, for example:
 * SWIM requests put the payload in the description, the notes for ops is another field
 * in its sidecar table.
 */
export const TASK_TYPES_WITH_INSTRUCTIONS = new Set<TaskTypeName>([
  // internal
  'self_reminder',
  'general',
  // Rejected-fee tasks are created by Advisory Ops (the ops dashboard) and carry
  // the per-account detail (account, fee, action) in their description. Without
  // this the advisor sees only the title and no instructions.
  'rejected_fee',
  // Launched workflow tasks behave like general tasks; they carry the rich-text
  // description copied from their workflow template.
  'workflow',

  // investment ops
  'alternative_investments_purchase',
  'investment_management',
  'investment_management_direct_indexing',
  'investment_management_wealthcare',

  // advisor ops
  // These carry the advisor's "Notes for Ops" in the description, the same as the
  // client-services types below — the note is the request, so Ops needs to read it.
  'client_billing',
  'corporate_retirement_plan',

  // client services
  'account_maintenance',
  'open_new_account',
  'transfer_of_assets',
  'client_info_change',
  'other',

  // client
  'account_setup',
  'client_other',
  // The household-level cockpit task. It is the only task an all-NFS cockpit household has, so
  // it is where the advisor's notes for Client Services land when there is no confirmation task.
  'eci_household_onboarding',

  // deprecated
  'contract_change',
  'contract',
  'data_info_maintenance',
  'emoney',
  'financial_planning',
  'marketing',
  'reporting',
  'transfer',
]);

export const CLIENT_REVIEW_TASK_TYPES = new Set<TaskTypeName>([
  'client_review',
  'client_review_meeting',
  'client_review_meeting_material_request',
  'client_review_presentation',
]);

export const INVESTMENT_PROPOSAL_TASK_TYPES = new Set<TaskTypeName>([
  'investment_proposal_generation',
  'investment_proposal_presentation',
  'investment_proposal_request',
]);

export const TASK_TYPES_THAT_CANNOT_BE_DELETED = new Set<TaskTypeName>([
  'swim_request',
  'money_movement_request',
  'trades_cash_management',
  'trading_request',
]);
